import { Injectable } from '@angular/core';
import swal, { SweetAlertResult } from 'sweetalert2';
import { SweetAlertOptions } from 'sweetalert2';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    /**
     * Displays an alert.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     */
    showAlert(message: string, title?: string): void {
        swal.fire({
            icon: 'warning',
            text: message,
            title
        });
    }

    /**
     * Displays an information.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     */
    showInfo(message: string, title?: string): void {
        swal.fire({
            icon: 'info',
            text: message,
            title
        });
    }

    /**
     * Displays an error.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     */
    showError(message: string, title?: string): void {
        swal.fire({
            icon: 'error',
            text: message,
            title
        });
    }

    /**
     * Displays a success message.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [callback] Callback executed if the user clicks on the button.
     */
    showSuccess(message: string, title?: string, callback?: any): void {
        const opt: SweetAlertOptions = {
            icon: 'success',
            text: message,
            title
        };

        swal.fire(opt)
            .then(() => {
                if (callback) {
                    callback();
                }
            })
            .catch(e => {});
    }

    /**
     * Displays a message requesting a confirmation from the user.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [callback] Callback executed if the user clicks on confirm.
     */
    showConfirm(
        message: string,
        title?: string,
        callback?: any,
        cancelButtonText = 'Cancel',
        confirmButtonText = 'Confirm'
    ): void {
        const opt: SweetAlertOptions = {
            icon: 'warning',
            text: message,
            title,
            cancelButtonText,
            confirmButtonText,
            showCancelButton: true,
            allowEscapeKey: false,
            allowOutsideClick: false
        };

        swal.fire(opt)
            .then(result => {
                if (result.value && callback) {
                    callback();
                }
            })
            .catch(e => {});
    }

    // Sometimes we want to handle the response and interact with it in another function so similar to previous but witout a callback function
    showCustomConfirm(
        message: string,
        title?: string,
        confirmButtonText = 'Confirm',
        cancelButtonText?: string
    ): Promise<SweetAlertResult<unknown>> {
        const opt: SweetAlertOptions = {
            icon: 'warning',
            text: message,
            title,
            cancelButtonText,
            confirmButtonText,
            showCancelButton: !!cancelButtonText,
            allowEscapeKey: false,
            allowOutsideClick: false
        };

        return swal.fire(opt);
    }
}
